import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import {
  guardarDadosUsuario,
  guardarDadosUsuario2,
  obterDadosUsuario2,
} from "./cookies";

const DEFAULT_API_ORIGIN = "https://api.controlox.com.br";

const trimTrailingSlashes = (value: string) => value.replace(/\/+$/, "");

const withTrailingSlash = (value: string) => `${trimTrailingSlashes(value)}/`;

const apiOrigin = trimTrailingSlashes(
  process.env.REACT_APP_API_ORIGIN || DEFAULT_API_ORIGIN
);

const buildApiBaseUrl = (
  apiBaseUrl: string | undefined,
  applicationPath: "Sistema" | "App"
) => withTrailingSlash(apiBaseUrl || `${apiOrigin}/${applicationPath}`);

export const api = axios.create({
  baseURL: buildApiBaseUrl(
    process.env.REACT_APP_SISTEMA_API_BASE_URL,
    "Sistema"
  ),
});

// Todas as chamadas autenticadas compartilham a mesma renovação. Isso evita que
// requisições simultâneas tentem usar um token de renovação já rotacionado.
let renovacaoEmAndamento: Promise<AxiosResponse> | null = null;

type RequisicaoComRetry = InternalAxiosRequestConfig & { _retry?: boolean };

const isRefreshRequest = (url?: string) =>
  url?.includes("/Usuario/RenovarTokens") ?? false;

export const renovarTokens = (
  tokenAcesso: string | boolean,
  tokenRenovacao: string | boolean
): Promise<AxiosResponse> => {
  if (renovacaoEmAndamento) return renovacaoEmAndamento;

  const dados = obterDadosUsuario2();

  // Outra chamada (ou aba) pode ter renovado a sessão desde que a tela leu o token.
  if (typeof tokenAcesso === "string" && dados.token && dados.token !== tokenAcesso && dados.tokenRenovacao) {
    return Promise.resolve({
      data: {
        camposAdicionais: {
          tokenAcesso: dados.token,
          tokenRenovacao: dados.tokenRenovacao,
        },
      },
      status: 200,
      statusText: "OK",
      headers: {},
      config: {} as InternalAxiosRequestConfig,
    });
  }

  const acesso = dados.token || (typeof tokenAcesso === "string" ? tokenAcesso : null);
  const renovacao = dados.tokenRenovacao || (typeof tokenRenovacao === "string" ? tokenRenovacao : null);
  if (!acesso || !renovacao) return Promise.reject(new Error("Sessão expirada."));

  renovacaoEmAndamento = api.get("/Usuario/RenovarTokens", {
    params: { TokenAcesso: acesso, TokenRenovacao: renovacao },
  }).then((response) => {
    const { tokenAcesso: novoAcesso, tokenRenovacao: novaRenovacao } =
      response.data?.camposAdicionais || {};
    if (!novoAcesso || !novaRenovacao) throw new Error("Resposta de renovação inválida.");

    const usuario = obterDadosUsuario2();
    if (usuario.token !== acesso || usuario.tokenRenovacao !== renovacao) {
      throw new Error("A sessão foi alterada durante a renovação.");
    }
    guardarDadosUsuario(
      novoAcesso, novaRenovacao, usuario.email || "", usuario.id || "",
      usuario.urlAvatar || undefined
    );
    guardarDadosUsuario2(
      novoAcesso, novaRenovacao, usuario.email || "", usuario.id || "",
      usuario.urlAvatar || undefined
    );
    return response;
  }).finally(() => {
    renovacaoEmAndamento = null;
  });

  return renovacaoEmAndamento;
};

api.interceptors.request.use(async (config) => {
  if (isRefreshRequest(config.url)) return config;
  const authorization = config.headers?.Authorization;
  if (typeof authorization !== "string" || !authorization.startsWith("Bearer ")) return config;

  if (renovacaoEmAndamento) await renovacaoEmAndamento;
  const tokenAtual = obterDadosUsuario2().token;
  if (tokenAtual) config.headers.Authorization = `Bearer ${tokenAtual}`;
  return config;
});

api.interceptors.response.use(undefined, async (error) => {
  const request = error.config as RequisicaoComRetry | undefined;
  if (
    error.response?.status !== 401 || !request || request._retry ||
    isRefreshRequest(request.url) ||
    typeof request.headers?.Authorization !== "string" ||
    !request.headers.Authorization.startsWith("Bearer ")
  ) return Promise.reject(error);

  request._retry = true;
  const tokenEnviado = request.headers.Authorization.slice("Bearer ".length);
  const usuario = obterDadosUsuario2();
  if (!usuario.tokenRenovacao) return Promise.reject(error);

  try {
    const tokenAtual = usuario.token === tokenEnviado
      ? (await renovarTokens(tokenEnviado, usuario.tokenRenovacao)).data.camposAdicionais.tokenAcesso
      : usuario.token;
    if (!tokenAtual) return Promise.reject(error);
    request.headers.Authorization = `Bearer ${tokenAtual}`;
    return api(request);
  } catch (refreshError) {
    return Promise.reject(refreshError);
  }
});

export const apiApp = axios.create({
  baseURL: buildApiBaseUrl(
    process.env.REACT_APP_APP_API_BASE_URL,
    "App"
  ),
});

export const apiExternaCep = axios.create({
  baseURL: `https://viacep.com.br`,
});

export const buscarDadosCep = (cep: string) => {
  const url = `/ws/${cep}/json/`;
  return apiExternaCep.get(url);
};

export const apiAwesomeAPIDeMoedas = axios.create({
  baseURL: "https://economia.awesomeapi.com.br/",
});

export const obterValorDoReal = () => {
  const conversao = "USD-BRL";
  //opções EUR-BRL, BTC-BRL
  const url = `last/${conversao}`;

  return apiAwesomeAPIDeMoedas.get(url, { responseType: "json" });
};
